import { useEffect, useState } from "react";
import { useHistory, useParams } from "react-router-dom";

import { FormikHelpers } from "formik";

import { INotificationCreateDTO } from "@modules/notifications/model/INotifications";

import ROUTES from "@config/routes";
import { convertToUTC } from "@pages/Admin/Notifications/resource/table/table.helpers";

import {
  INotificationFormValues,
  formInitialValues,
} from "../schema/notifications.schema";
import { useNotificationContext } from "../../../context";

import { INotificationFormMode, CREATE, EDIT, VIEW } from "./interface";

export const useNotificationForm = (mode: INotificationFormMode) => {
  const isCreate = mode === CREATE;
  const isEdit = mode === EDIT;
  const isView = mode === VIEW;

  const { id } = useParams<{ id: string }>();
  const { actions } = useNotificationContext();

  const history = useHistory();

  const [loading, setLoading] = useState(false);
  const [initialValues, setInitialValues] = useState<INotificationFormValues>();

  const [previewOpen, setPreviewOpen] = useState(false);

  const buildPayload = async (
    values: INotificationFormValues
  ): Promise<INotificationCreateDTO> => {
    return {
      title: values.title,
      content: values.content,
      centers: values.centers,
      segments: values.segments,
      profiles: values.profiles,
      startAt: convertToUTC(values.startAt),
      expireAt: convertToUTC(values.expireAt),
    };
  };

  const handleSubmit = async (
    values: INotificationFormValues,
    helpers: FormikHelpers<INotificationFormValues>
  ) => {
    if (isView) return;
    setLoading(true);
    try {
      const payload = await buildPayload(values);

      isEdit && id && (await actions.edit(id, payload));

      isCreate && (await actions.create(payload));

      handleRedirectAfterSubmit(values.startAt);
    } catch (error) {
      helpers.setSubmitting(false);
    } finally {
      setLoading(false);
    }
  };

  const handleRedirectAfterSubmit = (date: string) => {
    const now = new Date();
    const start = new Date(date);

    const tabParam = start > now ? "programadas" : "ativas";

    // NOTE: Redireciona para a lista já com a aba correta
    history.push(`${ROUTES.ADMIN_ROUTES.NOTIFICATIONS.ROOT}?tab=${tabParam}`);
  };

  const handlePreview = (values: INotificationFormValues) => {
    setPreviewOpen(true);
  };

  const handleCancelOrClose = () => {
    history.push(ROUTES.ADMIN_ROUTES.NOTIFICATIONS.ROOT);
  };

  useEffect(() => {
    if (isCreate) {
      return setInitialValues(formInitialValues);
    }

    if ((isEdit || isView) && id) {
      const load = async () => {
        const notificationFind = await actions.getById(id);
        if (notificationFind) {
          setInitialValues({
            title: notificationFind.title,
            content: notificationFind.content,
            centers: notificationFind.centers,
            segments: notificationFind.segments,
            profiles: notificationFind.profiles,
            startAt: notificationFind.startAt,
            expireAt: notificationFind.expireAt,
          });
        }
      };
      void load();
    }
  }, [isCreate, isEdit, isView, actions, id]);

  return {
    state: {
      loading,
      initialValues,
      previewOpen,
      isCreate,
      isEdit,
      isView,
      mode,
    },
    actions: {
      handleCancelOrClose,
      handleSubmit,
      handlePreview,
      closePreview: () => setPreviewOpen(false),
    },
  };
};
